/**
 * The single global store. OWNER: W-C.
 *
 * Everything cross-cutting lives here — active tab, current case, running job,
 * log buffer, system status, preferences, panel geometry, tree selection — so
 * that a feature workstream never has to own state two panels need to agree on.
 *
 * Two rules keep it cheap:
 *   1. Consumers subscribe through the focused hooks at the bottom of the file,
 *      never `useAppStore()` bare, so a log line does not re-render the ribbon.
 *   2. Derived values (`ribbonContext`) are assembled in a `useMemo` from
 *      primitive selectors rather than returned as a fresh object from the
 *      selector, which would make `useSyncExternalStore` loop forever.
 */

import { useMemo } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { UnlistenFn } from "@tauri-apps/api/event";

import {
  getPreferences,
  getSystemStatus,
  listJobs,
  onJobLog,
  onJobProgress,
  onJobStage,
  onJobState,
  setPreferences as ipcSetPreferences,
  splitError,
} from "@/ipc/api";
import type {
  CaseManifest,
  JobLogEntry,
  JobStatus,
  Preferences,
  SubjectScan,
  SystemStatus,
} from "@/ipc/contract";
import type { RibbonContext } from "@/ribbon/types";
import type { DockTabId, TabId } from "@/shell/tabRegistry";

// ------------------------------------------------------------------ types ---

/** The buffer holds contract entries verbatim; alias kept for readability. */
export type LogLine = JobLogEntry;

/** Job logs are unbounded upstream; the UI keeps a window of the tail. */
export const LOG_CAP = 5000;
/**
 * A real DWI run is mostly ANTs `DIAGNOSTIC,` iteration spam (271 of 865 lines
 * in the reference run). Capping `trace` separately means a long registration
 * cannot evict the stage banners that make the log readable.
 */
export const TRACE_CAP = 2000;

/** Drops oldest trace lines first, then the oldest lines of any level. */
function capBuffer(lines: JobLogEntry[]): JobLogEntry[] {
  let traces = 0;
  for (const l of lines) if (l.level === "trace") traces += 1;

  let out = lines;
  if (traces > TRACE_CAP) {
    let drop = traces - TRACE_CAP;
    out = [];
    for (const l of lines) {
      if (drop > 0 && l.level === "trace") {
        drop -= 1;
        continue;
      }
      out.push(l);
    }
  }
  return out.length > LOG_CAP ? out.slice(out.length - LOG_CAP) : out;
}

export type RibbonMode = "pinned" | "collapsed" | "autohide";

export interface PanelState {
  leftWidth: number;
  rightWidth: number;
  bottomHeight: number;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  bottomCollapsed: boolean;
}

export const DEFAULT_PANELS: PanelState = {
  leftWidth: 260,
  rightWidth: 300,
  bottomHeight: 220,
  leftCollapsed: false,
  rightCollapsed: false,
  bottomCollapsed: true,
};

export interface AppNotification {
  id: string;
  ts: string;
  title: string;
  detail?: string;
  tone: "info" | "success" | "warning" | "error";
  read: boolean;
}

/** Ribbon toggle buttons are keyed by `RibbonItem.id`. */
export type ToggleMap = Record<string, boolean>;

const DEFAULT_TOGGLES: ToggleMap = {
  "view.overlay": true,
  "view.crosshairs": true,
  "view.linkViews": true,
  "view.panel.studyExplorer": true,
  "view.panel.inspector": true,
  "developer.cleanup": false,
};

interface AppState {
  // --- navigation
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;

  // --- case / data
  currentCase: CaseManifest | null;
  setCurrentCase: (manifest: CaseManifest | null) => void;
  currentScan: SubjectScan | null;
  setCurrentScan: (scan: SubjectScan | null) => void;
  selectedNodeId: string | null;
  selectedNodeIds: string[];
  setSelectedNodeId: (id: string | null) => void;
  selectNode: (id: string, mode: "replace" | "toggle" | "range", orderedIds: string[]) => void;

  // --- execution
  /**
   * The run the workspace follows: its stages fill the Pipeline tab, its log
   * fills the dock, and the viewer follows its stages. One of `jobs`.
   */
  job: JobStatus | null;
  /**
   * Every run this session started and has not forgotten, by id. A batch can
   * have several in flight — `concurrency.limit` of them — and events for a run
   * that is not in focus used to be dropped on the floor, which made the second
   * subject of a parallel batch look frozen.
   */
  jobs: Record<string, JobStatus>;
  setJob: (job: JobStatus | null) => void;
  patchJob: (patch: Partial<JobStatus>) => void;
  /** Patch one run by id, whether or not it is the one in focus. */
  patchJobById: (jobId: string, patch: Partial<JobStatus>) => void;
  /** Bring a run into focus; its log replaces the dock's. */
  focusJob: (jobId: string) => void;
  logLines: JobLogEntry[];
  /** Batched by contract: one `set()` per flush, never per line. */
  appendLog: (entries: JobLogEntry[]) => void;
  clearLog: () => void;

  /**
   * Explicit override for "this case has results". `null` means "derive it",
   * which covers the common path; the Results workstream sets it after a real
   * `get_results_summary` call.
   */
  resultsAvailable: boolean | null;
  setResultsAvailable: (value: boolean | null) => void;

  // --- system
  systemStatus: SystemStatus | null;
  systemStatusError: string | null;
  refreshSystemStatus: (force?: boolean) => Promise<void>;

  // --- preferences
  preferences: Preferences | null;
  loadPreferences: () => Promise<void>;
  updatePreferences: (patch: Partial<Preferences>) => Promise<void>;

  // --- layout
  panels: PanelState;
  setPanelSize: (key: "leftWidth" | "rightWidth" | "bottomHeight", px: number) => void;
  togglePanel: (side: "left" | "right" | "bottom", collapsed?: boolean) => void;
  dockTab: DockTabId;
  setDockTab: (tab: DockTabId) => void;

  /** pinned = band always on; collapsed = hidden until a menu tab is clicked; autohide = hover. */
  ribbonMode: RibbonMode;
  setRibbonMode: (mode: RibbonMode) => void;

  // --- ribbon toggles
  toggles: ToggleMap;
  setToggle: (id: string, value?: boolean) => void;

  // --- notifications
  notifications: AppNotification[];
  notify: (n: Omit<AppNotification, "id" | "ts" | "read">) => void;
  markNotificationsRead: () => void;
  clearNotifications: () => void;
}

// Panel geometry is a user preference in practice: losing it on every restart
// is the kind of small rudeness desktop users notice.
const PERSIST_KEY = "openads.shell.v2";

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      activeTab: "home",
      setActiveTab: (tab) => set({ activeTab: tab }),

      currentCase: null,
      setCurrentCase: (manifest) =>
        set({ currentCase: manifest, resultsAvailable: null, selectedNodeId: null, selectedNodeIds: [] }),
      currentScan: null,
      setCurrentScan: (scan) => set({ currentScan: scan }),
      selectedNodeId: null,
      selectedNodeIds: [],
      setSelectedNodeId: (id) => set({ selectedNodeId: id, selectedNodeIds: id ? [id] : [] }),
      selectNode: (id, mode, orderedIds) => {
        const selectedNodeId = get().selectedNodeId;
        const selectedNodeIds = get().selectedNodeIds ?? [];
        if (mode === "toggle") {
          const has = selectedNodeIds.includes(id);
          const next = has ? selectedNodeIds.filter((entry) => entry !== id) : [...selectedNodeIds, id];
          if (next.length === 0) {
            set({ selectedNodeId: id, selectedNodeIds: [id] });
            return;
          }
          set({
            selectedNodeIds: next,
            selectedNodeId: has ? (selectedNodeId === id ? (next[next.length - 1] ?? id) : selectedNodeId) : id,
          });
          return;
        }
        if (mode === "range") {
          const anchor = selectedNodeId ?? id;
          const a = orderedIds.indexOf(anchor);
          const b = orderedIds.indexOf(id);
          if (a < 0 || b < 0) {
            set({ selectedNodeId: id, selectedNodeIds: [id] });
            return;
          }
          const lo = Math.min(a, b);
          const hi = Math.max(a, b);
          set({ selectedNodeId: id, selectedNodeIds: orderedIds.slice(lo, hi + 1) });
          return;
        }
        set({ selectedNodeId: id, selectedNodeIds: [id] });
      },

      job: null,
      jobs: {},
      setJob: (job) =>
        set((state) => ({
          job,
          jobs: job ? { ...state.jobs, [job.jobId]: job } : state.jobs,
        })),
      patchJob: (patch) => {
        const job = get().job;
        if (!job) return;
        get().patchJobById(job.jobId, patch);
      },
      patchJobById: (jobId, patch) =>
        set((state) => {
          const existing = state.jobs[jobId] ?? (state.job?.jobId === jobId ? state.job : null);
          if (!existing) return {};
          const merged = { ...existing, ...patch };
          return {
            jobs: { ...state.jobs, [jobId]: merged },
            // The focus is mirrored rather than derived, so the twenty-odd
            // places that read `job` keep working unchanged.
            job: state.job?.jobId === jobId ? merged : state.job,
          };
        }),
      focusJob: (jobId) =>
        set((state) => {
          const job = state.jobs[jobId];
          return job ? { job } : {};
        }),

      logLines: [],
      appendLog: (entries) => {
        if (entries.length === 0) return;
        const next = get().logLines.concat(entries);
        set({ logLines: next.length > LOG_CAP ? capBuffer(next) : next });
      },
      clearLog: () => set({ logLines: [] }),

      resultsAvailable: null,
      setResultsAvailable: (value) => set({ resultsAvailable: value }),

      systemStatus: null,
      systemStatusError: null,
      refreshSystemStatus: async (force = false) => {
        try {
          const status = await getSystemStatus(force);
          set({ systemStatus: status, systemStatusError: null });
        } catch (error) {
          set({ systemStatusError: splitError(error).message });
        }
      },

      preferences: null,
      loadPreferences: async () => {
        try {
          const prefs = await getPreferences();
          set({ preferences: prefs, toggles: { ...get().toggles, "developer.cleanup": prefs.cleanupEnabled } });
        } catch (error) {
          console.warn("[store] preferences unavailable", error);
        }
      },
      updatePreferences: async (patch) => {
        try {
          const prefs = await ipcSetPreferences(patch);
          set({ preferences: prefs });
        } catch (error) {
          get().notify({
            title: "Could not save preferences",
            detail: splitError(error).message,
            tone: "error",
          });
        }
      },

      panels: DEFAULT_PANELS,
      setPanelSize: (key, px) => set({ panels: { ...get().panels, [key]: Math.round(px) } }),
      togglePanel: (side, collapsed) => {
        const panels = get().panels;
        const key = `${side}Collapsed` as const;
        set({ panels: { ...panels, [key]: collapsed ?? !panels[key] } });
      },
      dockTab: "jobs",
      setDockTab: (tab) => set({ dockTab: tab }),
      ribbonMode: "pinned" as RibbonMode,
      setRibbonMode: (mode) => set({ ribbonMode: mode }),

      toggles: DEFAULT_TOGGLES,
      setToggle: (id, value) => {
        const toggles = get().toggles;
        set({ toggles: { ...toggles, [id]: value ?? !toggles[id] } });
      },

      notifications: [],
      notify: (n) =>
        set({
          notifications: [
            { ...n, id: crypto.randomUUID(), ts: new Date().toISOString(), read: false },
            ...get().notifications,
          ].slice(0, 50),
        }),
      markNotificationsRead: () =>
        set({ notifications: get().notifications.map((n) => ({ ...n, read: true })) }),
      clearNotifications: () => set({ notifications: [] }),
    }),
    {
      name: PERSIST_KEY,
      // Only geometry and the last tab survive a restart. Case/job state is
      // authoritative on the Rust side and must be re-fetched, not restored.
      //
      // `ribbonMode` is deliberately absent. Hiding the toolbar is a momentary
      // "I need the room" action, not a lasting preference, and a session that
      // starts with it hidden starts with no visible way to run anything. Every
      // launch begins pinned. Version 2 exists to throw away the mode a v1
      // client had already written.
      partialize: (state) => ({
        panels: state.panels,
        activeTab: state.activeTab,
        dockTab: state.dockTab,
        toggles: state.toggles,
      }),
      version: 2,
      migrate: (persisted) => {
        if (persisted && typeof persisted === "object" && "ribbonMode" in persisted) {
          const { ribbonMode: _dropped, ...rest } = persisted as Record<string, unknown>;
          return rest;
        }
        return persisted;
      },
    },
  ),
);

// ------------------------------------------------------------ derivations ---

function deriveHasResults(state: AppState): boolean {
  if (state.resultsAvailable !== null) return state.resultsAvailable;
  if (state.job?.state === "completed") return true;
  return (state.currentCase?.reports.length ?? 0) > 0;
}

/** Imperative read for non-React code (action handlers, event plumbing). */
export function getRibbonContext(): RibbonContext {
  const s = useAppStore.getState();
  return {
    hasCase: s.currentCase !== null,
    caseState: s.job?.state ?? "not_created",
    jobRunning: s.job?.state === "running" || s.job?.state === "queued",
    hasResults: deriveHasResults(s),
    engineReady: s.systemStatus?.engine.ready === true,
    engineKind: s.systemStatus?.engine.kind ?? "native",
    activeView: s.activeTab,
  };
}

// --------------------------------------------------------- selector hooks ---
// Each hook returns a primitive or a stable reference so consumers only
// re-render when the thing they actually read changes.

export const useActiveTab = () => useAppStore((s) => s.activeTab);
export const useCurrentCase = () => useAppStore((s) => s.currentCase);
export const useCurrentScan = () => useAppStore((s) => s.currentScan);
export const useSelectedNodeId = () => useAppStore((s) => s.selectedNodeId);
export const useSelectedNodeIds = () => useAppStore((s) => s.selectedNodeIds ?? []);
export const useJob = () => useAppStore((s) => s.job);
/**
 * Every run in flight, oldest first. Empty almost always; two or more only
 * during a batch on a machine with the cores for it.
 *
 * The filtering happens in `useMemo`, not in the selector. A zustand v5 selector
 * that builds a fresh array on every call never compares equal to the last one, so
 * `useSyncExternalStore` re-renders forever and React tears the tree down — a
 * white window with a title bar and nothing else. `s.jobs` is a stable
 * reference, replaced only when a job actually changes, so deriving from it is
 * both correct and cheap.
 */
export const useRunningJobs = () => {
  const jobs = useAppStore((s) => s.jobs);
  return useMemo(
    () => Object.values(jobs).filter((j) => j.state === "running" || j.state === "queued"),
    [jobs],
  );
};
export const useJobRunning = () =>
  useAppStore((s) => s.job?.state === "running" || s.job?.state === "queued");
export const useLogLines = () => useAppStore((s) => s.logLines);
export const useSystemStatus = () => useAppStore((s) => s.systemStatus);
export const useSystemStatusError = () => useAppStore((s) => s.systemStatusError);
export const usePreferences = () => useAppStore((s) => s.preferences);
export const usePanels = () => useAppStore((s) => s.panels);
export const useDockTab = () => useAppStore((s) => s.dockTab);
export const useRibbonMode = () => useAppStore((s) => s.ribbonMode ?? "pinned");
export const useToggles = () => useAppStore((s) => s.toggles);
export const useToggle = (id: string) => useAppStore((s) => s.toggles[id] ?? false);
export const useNotifications = () => useAppStore((s) => s.notifications);

/** Actions are stable for the store's lifetime; read them without subscribing. */
export const appActions = {
  setActiveTab: (tab: TabId) => useAppStore.getState().setActiveTab(tab),
  setCurrentCase: (m: CaseManifest | null) => useAppStore.getState().setCurrentCase(m),
  setCurrentScan: (s: SubjectScan | null) => useAppStore.getState().setCurrentScan(s),
  setSelectedNodeId: (id: string | null) => useAppStore.getState().setSelectedNodeId(id),
  selectNode: (id: string, mode: "replace" | "toggle" | "range", orderedIds: string[]) =>
    useAppStore.getState().selectNode(id, mode, orderedIds),
  setJob: (j: JobStatus | null) => useAppStore.getState().setJob(j),
  clearLog: () => useAppStore.getState().clearLog(),
  setResultsAvailable: (v: boolean | null) => useAppStore.getState().setResultsAvailable(v),
  refreshSystemStatus: (force?: boolean) => useAppStore.getState().refreshSystemStatus(force),
  updatePreferences: (p: Partial<Preferences>) => useAppStore.getState().updatePreferences(p),
  togglePanel: (side: "left" | "right" | "bottom", collapsed?: boolean) =>
    useAppStore.getState().togglePanel(side, collapsed),
  setDockTab: (tab: DockTabId) => useAppStore.getState().setDockTab(tab),
  setRibbonMode: (mode: RibbonMode) => useAppStore.getState().setRibbonMode(mode),
  setToggle: (id: string, value?: boolean) => useAppStore.getState().setToggle(id, value),
  notify: (n: Omit<AppNotification, "id" | "ts" | "read">) => useAppStore.getState().notify(n),
};

/**
 * The ribbon's enable/disable input. Assembled from primitive subscriptions so
 * the identity only changes when one of the six fields actually changes.
 */
export function useRibbonContext(): RibbonContext {
  const hasCase = useAppStore((s) => s.currentCase !== null);
  const caseState = useAppStore((s) => s.job?.state ?? ("not_created" as const));
  const jobRunning = useJobRunning();
  const hasResults = useAppStore(deriveHasResults);
  const engineReady = useAppStore((s) => s.systemStatus?.engine.ready === true);
  const engineKind = useAppStore((s) => s.systemStatus?.engine.kind ?? "native");
  const activeView = useAppStore((s) => s.activeTab);
  return useMemo(
    () => ({ hasCase, caseState, jobRunning, hasResults, engineReady, engineKind, activeView }),
    [hasCase, caseState, jobRunning, hasResults, engineReady, engineKind, activeView],
  );
}

// ------------------------------------------------------ event subscriptions ---

/**
 * Wires the four `job://*` events into the store, once, for the whole app.
 * Called from `App`; no other workstream should call `onJobLog` & co.
 * Returns a disposer. Safe under StrictMode double-invocation.
 */
/**
 * Make the live job agree with the backend.
 *
 * The job registry lives in the backend process. Restart it — a rebuild during
 * development, a crash, a machine waking with the app open — and every job it
 * held is gone, while this side is still holding a `JobStatus` that says
 * "running". Nothing arrives to correct it, because the thing that would have
 * sent the event is what went away.
 *
 * The result is a run that cannot be stopped: Stop asks the backend to cancel
 * a job it has never heard of, and the interface goes on claiming progress on
 * something that is not happening. A status line that cannot be wrong about
 * *this* is most of what a status line is for.
 *
 * So the job is checked against the backend at startup and whenever the window
 * is focused again, and a job the backend does not know is not running.
 */
/**
 * Correct runs this window believes are still going.
 *
 * The registry lives in memory on the Rust side, so a backend restart — or a
 * rebuild during development — leaves the frontend holding a `running` job that
 * no longer exists anywhere. Called at startup and whenever the window comes
 * back to the front, which is exactly when that has happened.
 *
 * All of them, not just the one in focus: a batch can have several in flight and
 * an orphan among them would leave the batch waiting for a slot forever.
 */
export async function reconcileJob(): Promise<void> {
  const state = useAppStore.getState();
  const live = Object.values(state.jobs).filter(
    (j) => j.state === "running" || j.state === "queued",
  );
  if (live.length === 0) return;
  try {
    const known = await listJobs(undefined, 50);
    let orphaned = 0;
    for (const job of live) {
      const match = known.find((j) => j.jobId === job.jobId);
      if (!match) {
        orphaned += 1;
        // Not removed from the map: a run the reader watched should still be
        // there to look at. Marked for what it is instead.
        useAppStore.getState().patchJobById(job.jobId, {
          state: "failed",
          errorMessage:
            "This run is not known to the engine any more — it ended while the app was not listening.",
          finishedIso: new Date().toISOString(),
        });
        continue;
      }
      if (match.state !== job.state) {
        useAppStore.getState().patchJobById(job.jobId, match);
      }
    }
    if (orphaned > 0) {
      useAppStore.getState().notify({
        title:
          orphaned === 1
            ? "A run is no longer being tracked"
            : `${orphaned} runs are no longer being tracked`,
        detail:
          "The engine does not know them any more — they ended while the app was not listening. Nothing is running now.",
        tone: "warning",
      });
    }
  } catch {
    // An unreachable backend is its own, louder problem; leave the jobs alone
    // rather than declaring them dead on a failed call.
  }
}

export function initJobSubscriptions(): () => void {
  let cancelled = false;
  const unlisteners: UnlistenFn[] = [];

  const track = (p: Promise<UnlistenFn>) =>
    void p
      .then((un) => {
        if (cancelled) un();
        else unlisteners.push(un);
      })
      .catch((error) => console.warn("[store] job event subscribe failed", error));

  // Correct a stale job now, and whenever this window comes back to the front
  // after having been away — which is exactly when a backend restart happened.
  void reconcileJob();
  const onFocus = () => void reconcileJob();
  window.addEventListener("focus", onFocus);
  unlisteners.push(() => window.removeEventListener("focus", onFocus));

  track(
    onJobLog((e) => {
      // The dock shows one run's log, so lines from a sibling in a parallel
      // batch are dropped here rather than interleaved into it. They are still
      // on disk in that run's own log file, and following it is a matter of
      // bringing it into focus.
      const { appendLog, job } = useAppStore.getState();
      if (job && e.jobId !== job.jobId) return;
      appendLog(e.entries);
    }),
  );

  track(
    onJobStage((e) => {
      const { jobs, patchJobById } = useAppStore.getState();
      const job = jobs[e.jobId];
      if (!job) return;
      patchJobById(e.jobId, {
        stages: job.stages.map((s) =>
          s.id === e.stage && s.modality === e.modality ? { ...s, state: e.state } : s,
        ),
        currentStage: e.state === "running" ? e.stage : job.currentStage,
        currentModality: e.state === "running" ? e.modality : job.currentModality,
      });
    }),
  );

  track(
    onJobProgress((e) => {
      const { jobs, patchJobById: patchJob } = useAppStore.getState();
      if (!jobs[e.jobId]) return;
      // `health` is null on ordinary progress events and on the event sent
      // when a silent stage speaks again, so assigning it unconditionally is
      // what clears a stale note.
      patchJob(e.jobId, {
        progressPct: e.progressPct,
        currentStage: e.currentStage,
        health: e.health,
      });
    }),
  );

  track(
    onJobState((e) => {
      const { jobs, patchJobById, notify } = useAppStore.getState();
      const job = jobs[e.jobId];
      if (!job) return;
      const patchJob = (patch: Partial<JobStatus>) => patchJobById(e.jobId, patch);
      patchJob({
        state: e.state,
        exitCode: e.exitCode,
        errorMessage: e.errorMessage,
        finishedIso:
          e.state === "completed" || e.state === "failed" || e.state === "cancelled"
            ? new Date().toISOString()
            : job.finishedIso,
      });
      if (e.state === "completed") {
        notify({ title: `Job finished — ${job.caseId}`, tone: "success" });
      } else if (e.state === "failed") {
        notify({ title: "Job failed", detail: e.errorMessage ?? undefined, tone: "error" });
      } else if (e.state === "cancelled") {
        notify({ title: "Job cancelled", tone: "warning" });
      }
    }),
  );

  return () => {
    cancelled = true;
    for (const un of unlisteners) un();
    unlisteners.length = 0;
  };
}
